import React, { useState, useEffect } from 'react';
import { AppData } from '../types';
import { AppIcon } from '../components/AppIcon';
import { StoreBadge } from '../components/StoreBadge';
import { appCatalog } from '../services/appCatalogService';
import { Globe, Star, ArrowRight, Loader2, TrendingUp, ShieldCheck } from 'lucide-react';

interface MarketPageViewProps {
  marketCode?: string | null;
  onSelectApp: (appId: string) => void;
  onNavigate: (route: string) => void;
}

const ALL_MARKETS = [
  { code: 'US', name: 'United States', flag: '🇺🇸', currency: 'USD ($)', language: 'English' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽', currency: 'MXN ($)', language: 'Spanish' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', currency: 'CAD ($)', language: 'English / French' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', currency: 'GBP (£)', language: 'English' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', currency: 'EUR (€)', language: 'Spanish' },
  { code: 'FR', name: 'France', flag: '🇫🇷', currency: 'EUR (€)', language: 'French' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', currency: 'EUR (€)', language: 'German' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', currency: 'EUR (€)', language: 'Italian' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷', currency: 'BRL (R$)', language: 'Portuguese' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', currency: 'JPY (¥)', language: 'Japanese' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', currency: 'KRW (₩)', language: 'Korean' },
  { code: 'IN', name: 'India', flag: '🇮🇳', currency: 'INR (₹)', language: 'English / Hindi' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', currency: 'AUD ($)', language: 'English' }
];

export const MarketPageView: React.FC<MarketPageViewProps> = ({
  marketCode,
  onSelectApp,
  onNavigate
}) => {
  const [apps, setApps] = useState<AppData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const activeMarket = ALL_MARKETS.find((m) => m.code.toUpperCase() === (marketCode || '').toUpperCase()) || (marketCode ? {
    code: marketCode.toUpperCase(),
    name: marketCode.toUpperCase(),
    flag: '🌐',
    currency: 'Local',
    language: 'Native'
  } : null);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const country = (marketCode || 'US').toUpperCase();

    appCatalog
      .searchApps({
        country,
        category: 'all',
        store: 'all',
        sort: 'trending',
        page: 1,
        limit: 50
      })
      .then((res) => {
        if (isMounted) {
          setApps(res.results || []);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [marketCode]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-slate-200/80">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold mb-2">
            <Globe className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>International Storefront Markets</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-950 tracking-tight">
            {activeMarket ? `${activeMarket.flag} ${activeMarket.name} Storefront Market` : 'Global App Store Markets'}
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            {activeMarket
              ? `Real-time search observations, storefront pricing (${activeMarket.currency}), and localized ratings indexed for ${activeMarket.name}.`
              : 'Explore AppStatsView multi-country index across 13 core international App Store regions.'}
          </p>
        </div>

        {activeMarket && (
          <button
            onClick={() => onNavigate('/markets')}
            className="text-xs font-bold text-blue-600 hover:underline cursor-pointer"
          >
            ← View all markets
          </button>
        )}
      </div>

      {/* Active Market App Grid */}
      {activeMarket ? (
        isLoading ? (
          <div className="py-24 flex flex-col items-center justify-center space-y-3">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="text-xs text-slate-500 font-medium">Loading market storefront index...</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="p-5 bg-white rounded-3xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{activeMarket.flag}</span>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">{activeMarket.name} App Store</h3>
                  <span className="text-xs text-slate-500">Official Store Currency: {activeMarket.currency} • Language: {activeMarket.language}</span>
                </div>
              </div>

              <span className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold self-start sm:self-center">
                Storefront Ingestion Active
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {apps.map((app) => (
                <div
                  key={app.id}
                  onClick={() => onSelectApp(app.appStoreId ? String(app.appStoreId) : app.id)}
                  className="bg-white rounded-3xl p-5 border border-slate-200/80 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer flex flex-col justify-between group space-y-4"
                >
                  <div className="flex items-start gap-3.5">
                    <AppIcon app={app} size="lg" className="w-14 h-14 rounded-2xl flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <h4 className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors truncate">
                          {app.name}
                        </h4>
                        <StoreBadge store={app.primaryStore} size="xs" />
                      </div>
                      <p className="text-xs text-slate-500 truncate">{app.developer}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                        <span className="flex items-center gap-1 font-bold text-amber-500">
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                          <span>{app.rating.toFixed(1)}</span>
                        </span>
                        <span>•</span>
                        <span>{activeMarket.flag} Search Pos #{app.currentRank}</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                    <span className="font-mono text-slate-400 text-[11px]">{app.category}</span>
                    <span className="font-bold text-blue-600 group-hover:underline flex items-center gap-1">
                      <span>Inspect</span>
                      <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ) : (
        /* Markets Hub Grid */
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {ALL_MARKETS.map((m) => (
            <div
              key={m.code}
              onClick={() => onNavigate(`/market/${m.code.toLowerCase()}`)}
              className="bg-white rounded-3xl p-5 border border-slate-200/80 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer space-y-3 group"
            >
              <div className="flex items-center justify-between">
                <span className="text-3xl">{m.flag}</span>
                <span className="text-xs font-mono font-bold text-slate-400">{m.code}</span>
              </div>
              <div>
                <h3 className="text-base font-black text-slate-900 group-hover:text-blue-600 transition-colors">
                  {m.name}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {m.currency} • {m.language}
                </p>
              </div>
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] font-bold text-blue-600">
                <span>View Storefront</span>
                <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
